import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { determinePublicationStatus } from '@wilson/activities/pipes';
import {
  Activity,
  ActivityWithCategoryAndLocationsAndReportedDetails,
  Shift,
  WithPreparedAttributes,
} from '@wilson/interfaces';
import {
  ShiftDrawerCloseHelperService,
  ShiftIdWithPosition,
  ShiftMultiSelectHelperService,
} from '@wilson/shift-timeline/assignments-gantt-chart';
import {
  AddShiftWithoutActivitiesToCellViewUnassignedRegion,
  AddShiftWithoutActivitiesToUnassignedRegion,
  RemoveShiftWithoutActivitiesFromCellViewUnassignedRegion,
  RemoveShiftWithoutActivitiesFromUnassignedRegion,
  SetDemarcatorRange,
  ShiftTimelineDataTransformerService,
} from '@wilson/shift-timeline/state';
import { ShiftsService } from '@wilson/shifts';
import { NzMessageService } from 'ng-zorro-antd/message';
import { distinctUntilChanged, firstValueFrom, map, Observable } from 'rxjs';
import { DeleteShiftCommonOperationsService } from './delete-shift-common-operations.service';
import { TimelineCommonOperationsService } from './timeline-common-operations.service';

export type SelectedShift = Shift &
  WithPreparedAttributes & { id: string } & {
    activities: ActivityWithCategoryAndLocationsAndReportedDetails[];
    plannedDuration: number;
    reportedDuration: number;
  };

@Injectable({ providedIn: 'root' })
export class ShiftDrawerService {
  private readonly translate = inject(TranslateService);
  private readonly message = inject(NzMessageService);
  private readonly store = inject(Store);
  private readonly shiftsServices = inject(ShiftsService);
  private readonly shiftTimelineDataTransformerService = inject(
    ShiftTimelineDataTransformerService,
  );
  private readonly deleteShiftCommonOperationsService = inject(
    DeleteShiftCommonOperationsService,
  );
  private readonly timelineCommonOperationsService = inject(
    TimelineCommonOperationsService,
  );
  private readonly shiftMultiSelectHelperService = inject(
    ShiftMultiSelectHelperService,
  );
  private readonly shiftDrawerCloseHelperService = inject(
    ShiftDrawerCloseHelperService,
  );

  public isUnassignedShift$: Observable<boolean> =
    this.shiftMultiSelectHelperService.isSingleSelectedShiftUnassigned$;
  public isDrawerVisible$: Observable<boolean> =
    this.shiftMultiSelectHelperService.singleSelectedShiftIdWithPosition$.pipe(
      distinctUntilChanged(isSameSingleSelectedShiftIdWithPosition),
      map((shiftIdWithPosition) => !!shiftIdWithPosition),
    );
  public selectedShiftId$: Observable<string | null> =
    this.shiftMultiSelectHelperService.singleSelectedShiftIdWithPosition$.pipe(
      distinctUntilChanged(isSameSingleSelectedShiftIdWithPosition),
      map((shiftIdWithPosition) => shiftIdWithPosition?.id ?? null),
    );

  public closeDrawer(): void {
    this.shiftDrawerCloseHelperService.checkPristineAndCloseDrawer({
      onOkCallback: () => {
        this.shiftMultiSelectHelperService.clearSelection();
        this.store.dispatch([new SetDemarcatorRange([])]);
      },
    });
  }

  public updateUnassignedShifts(shiftToUpdate: SelectedShift): void {
    const convertedShift =
      this.shiftTimelineDataTransformerService.convertShiftToShiftWithoutActivities(
        shiftToUpdate as Shift &
          WithPreparedAttributes & { activities: Activity[]; id: string },
      );

    const transformedShift =
      this.shiftTimelineDataTransformerService.convertToUnassignedShift(
        convertedShift as Shift &
          WithPreparedAttributes & { activities: Activity[]; id: string },
      );

    this.store.dispatch([
      new RemoveShiftWithoutActivitiesFromUnassignedRegion({
        shiftId: transformedShift.id,
      }),
      new AddShiftWithoutActivitiesToUnassignedRegion(transformedShift),
    ]);
  }

  public updateCellViewUnassignedShifts(
    shiftToUpdate: Shift &
      WithPreparedAttributes & {
        id: string;
      },
  ): void {
    const convertedShift =
      this.shiftTimelineDataTransformerService.convertShiftToShiftWithoutActivities(
        shiftToUpdate as Shift &
          WithPreparedAttributes & { activities: Activity[]; id: string },
      );

    const transformedShift =
      this.shiftTimelineDataTransformerService.convertToUnassignedShift(
        convertedShift as Shift &
          WithPreparedAttributes & { activities: Activity[]; id: string },
      );

    this.store.dispatch([
      new RemoveShiftWithoutActivitiesFromCellViewUnassignedRegion({
        shiftId: transformedShift.id,
      }),
      new AddShiftWithoutActivitiesToCellViewUnassignedRegion(transformedShift),
    ]);
  }

  async deleteShiftInCellViewMode(
    shift: Shift & { id: string },
  ): Promise<void> {
    await this.shiftsServices
      .deleteShift(shift.id as string)
      .then(() => {
        this.deleteShiftCommonOperationsService.handleCellViewDeleteOperation(
          shift,
        );
        this.shiftMultiSelectHelperService.clearSelection();
        this.message.success(
          this.translate.instant('general.delete.successful'),
        );
      })
      .catch(() => {
        this.message.error(
          this.translate.instant('general.something_went_wrong'),
        );
      });
  }

  // TODO:possibly delete this
  async deleteShiftInRegularMode(shift: Shift & { id: string }): Promise<void> {
    await this.shiftsServices
      .deleteShift(shift.id as string)
      .then(() => {
        this.deleteShiftCommonOperationsService.handleStandardViewDeleteOperation(
          shift,
        );
        this.shiftMultiSelectHelperService.clearSelection();
        this.store.dispatch([new SetDemarcatorRange([])]);
        this.message.success(
          this.translate.instant('general.delete.successful'),
        );
      })
      .catch(() => {
        this.message.error(
          this.translate.instant('general.something_went_wrong'),
        );
      });
  }

  public async updateShiftPublicationStatus(
    selectedShiftId: string,
  ): Promise<Shift> {
    const shift = (await firstValueFrom(
      this.timelineCommonOperationsService.fetchShiftWithTranslatedActivityCategoryAndSortActivities(
        selectedShiftId,
      ),
    )) as SelectedShift;
    const shiftPublicationStatusToUpdate = determinePublicationStatus(
      shift.activities,
      shift.publicationStatus,
    );

    const shiftToUpdate = {
      id: selectedShiftId,
      publicationStatus: shiftPublicationStatusToUpdate,
    };
    return firstValueFrom(this.shiftsServices.updateShift(shiftToUpdate));
  }
}

function isSameSingleSelectedShiftIdWithPosition(
  prev: ShiftIdWithPosition | null,
  curr: ShiftIdWithPosition | null,
): boolean {
  if (prev === null && curr === null) {
    return true;
  }
  if (prev === null || curr === null) {
    return false;
  }
  return prev.id === curr.id;
}
